import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import qs from 'qs';
import { Box, CircularProgress } from '@mui/material';

import logo from '@src/assets/logo.svg';

import { useAppDispatch } from '@src/redux/hooks';
import { setUser, cookieOptions } from '@src/redux/authenticator';
import { LutienApi } from '@src/services/lutien/lutien-api';
import { colors } from '@src/styles';

const getRedirectUri = (): string => {
  const origin = window.location.origin;
  return `${origin}/url/auth`;
};

export const UrlAuth = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  useEffect(() => {
    const run = async () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const code = params.get('code');
        if (!code) {
          navigate('/login?error=' + encodeURIComponent('Missing authorization code'), { replace: true });
          return;
        }

        const dataConfig = qs.stringify({
          grant_type: 'authorization_code',
          code,
          redirect_uri: getRedirectUri(),
          client_id: import.meta.env.VITE_COGNITO_SAML_POOL_CLIENT_ID,
        });

        const tokenUrl = `${import.meta.env.VITE_COGNITO_URL_DOMAIN}/oauth2/token`;
        const tokenResp = await axios.post(tokenUrl, dataConfig, {
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        });

        const accessToken = tokenResp.data?.access_token as string | undefined;
        const refreshToken = tokenResp.data?.refresh_token as string | undefined;
        if (!accessToken) {
          navigate('/login?error=' + encodeURIComponent('Invalid SSO response'), { replace: true });
          return;
        }

        const lutienApi = new LutienApi();
        const me = await lutienApi.v1.auth.getMe({ bearerToken: accessToken });

        dispatch(
          setUser({
            token: accessToken,
            refreshToken: refreshToken || null,
            info: { email: me.email, id: me.id },
          }),
        );

        // Also write cookies directly (mirrors fulfilled handler behavior)
        document.cookie = `token=${accessToken}; path=/; ${cookieOptions.secure ? 'Secure; ' : ''}SameSite=${cookieOptions.sameSite}; Max-Age=${cookieOptions.expires! * 24 * 60 * 60}`;
        if (refreshToken)
          document.cookie = `refreshToken=${refreshToken}; path=/; ${cookieOptions.secure ? 'Secure; ' : ''}SameSite=${cookieOptions.sameSite}; Max-Age=${cookieOptions.expires! * 24 * 60 * 60}`;
        document.cookie = `email=${me.email}; path=/; ${cookieOptions.secure ? 'Secure; ' : ''}SameSite=${cookieOptions.sameSite}; Max-Age=${cookieOptions.expires! * 24 * 60 * 60}`;
        document.cookie = `userId=${me.id}; path=/; ${cookieOptions.secure ? 'Secure; ' : ''}SameSite=${cookieOptions.sameSite}; Max-Age=${cookieOptions.expires! * 24 * 60 * 60}`;

        navigate('/', { replace: true });
      } catch {
        navigate('/login?error=' + encodeURIComponent('SSO login failed'), { replace: true });
      }
    };
    run();
  }, [dispatch, navigate]);

  return (
    <Box
      sx={{
        display: 'flex',
        height: '100vh',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: colors.background.primary,
      }}
    >
      <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress
          size={88}
          thickness={1.6}
          aria-label="Finalizando login"
          sx={{ color: colors.primary.yellow }}
        />
        <Box
          component="img"
          src={logo}
          alt=""
          sx={{
            position: 'absolute',
            width: 34,
            height: 35,
            animation: 'urlAuthPulse 1.6s ease-in-out infinite',
            '@keyframes urlAuthPulse': {
              '0%, 100%': { opacity: 0.45 },
              '50%': { opacity: 1 },
            },
          }}
        />
      </Box>
    </Box>
  );
};
